/**
 * Steps of a "How to use" box (`HowToCard`) about custom documents, read from
 * the `forms:docGuide.<key>.steps` translations and paired with icons in the
 * order of the translated steps.
 *
 * A step may carry `only: '<flag>'` in the translations: it is shown only when
 * that flag is set (e.g. `{ contract: true }` for contract templates), so the
 * guide never explains a field the user does not see.
 *
 * @param {Function} t        - i18next `t`.
 * @param {string}   key      - Guide under `docGuide` (e.g. 'template').
 * @param {Array<Function>} icons - One icon component per translated step.
 * @param {Array<Function|null>} [actions] - Optional click handler per step.
 * @param {Object<string, boolean>} [flags] - Which `only` steps to keep.
 * @returns {Array<{title: string, text?: string, icon?: Function, onClick?: Function}>}
 */
export const docGuideSteps = (t, key, icons = [], actions = [], flags = {}) => {
  const steps = t(`forms:docGuide.${key}.steps`, { returnObjects: true });
  // A key returned as text means the translations are not loaded yet.
  return (Array.isArray(steps) ? steps : [])
    .map((step, i) => ({
      ...step,
      icon: icons[i],
      onClick: actions[i] || undefined,
    }))
    .filter((step) => !step.only || flags[step.only])
    .map(({ only: _only, ...step }) => step);
};

/** The `{ title, steps }` section of a guide, for `HowToCard`. */
export const docGuideSection = (t, key, icons, actions, flags) => ({
  title: t(`forms:docGuide.${key}.title`),
  steps: docGuideSteps(t, key, icons, actions, flags),
});
